/**
 * Menu → Deals → Combos: the deal editor (F-DEAL-N1).
 *
 * A deal is an ordinary product (so tax, PCT inheritance, photos, the
 * catalogue sync and reports keep working) plus a structure: fixed items and
 * choices. Basic fields go through createProduct / updateProduct exactly like
 * the product form; the structure through putComboStructure, keeping the
 * existing group/option ids so open tickets stay linked.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ImageIcon, Plus, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useOnlineOrderingEnabled } from '@/hooks/useOnlineOrderingEnabled'
import { toastHelpers } from '@/lib/toast-helpers'
import { mainCutoutPatch } from '@/lib/productGallery'
import { menuPhotoFileToDataUrl } from '@/components/forms/product/ProductPhotoField'
import type { ComboStructureInput, ComboView } from '@/types/deals'
import { COMBOS_QUERY_KEY, ConfirmDialog, Field, ProductPicker, useDealsCatalog } from './DealsControls'
import { comboDraftProblem, errorText, separatePrice, type DraftComponent, type DraftOption, type DraftSlot } from './dealsText'

let uidSeq = 0
const uid = () => `k${++uidSeq}`

type RowComponent = DraftComponent & { key: string }
type RowOption = DraftOption & { key: string }
type RowSlot = Omit<DraftSlot, 'options'> & { key: string; options: RowOption[] }

function rowsFrom(combo: ComboView | null): { components: RowComponent[]; slots: RowSlot[] } {
  if (!combo) return { components: [{ key: uid(), product_id: '', quantity: 1 }], slots: [] }
  return {
    components: combo.components.map((c) => ({ key: uid(), product_id: c.product_id, quantity: c.quantity })),
    slots: combo.slots.map((s) => ({
      key: uid(),
      group_id: s.group_id,
      name: s.name,
      choose: s.choose,
      options: s.options.map((o) => ({ key: uid(), option_id: o.option_id, product_id: o.product_id, price_delta: o.price_delta })),
    })),
  }
}

export function ComboEditorDialog({
  open,
  onOpenChange,
  combo,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = a new deal */
  combo: ComboView | null
}) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const online = useOnlineOrderingEnabled()
  const catalog = useDealsCatalog()

  // A new deal that saved its product but not its structure becomes an edit
  // of that product, so "Save" again does not create a duplicate.
  const [productId, setProductId] = useState<string | null>(combo?.product_id ?? null)
  const [name, setName] = useState(combo?.name ?? '')
  const [price, setPrice] = useState(combo ? String(combo.price) : '')
  const [categoryId, setCategoryId] = useState(combo?.category_id ?? '')
  const [description, setDescription] = useState(combo?.description ?? '')
  const [imageUrl, setImageUrl] = useState(combo?.image_url ?? '')
  const [storefrontVisible, setStorefrontVisible] = useState(combo?.is_storefront_visible ?? true)
  const [available, setAvailable] = useState(combo?.is_available ?? true)
  const [components, setComponents] = useState<RowComponent[]>(() => rowsFrom(combo).components)
  const [slots, setSlots] = useState<RowSlot[]>(() => rowsFrom(combo).slots)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [creatingCategory, setCreatingCategory] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const savedImage = useRef(combo?.image_url ?? '')

  // Reset when the dialog opens, or opens on a different deal — not when the
  // combos list refetches underneath an open editor (that would wipe edits).
  const openedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      openedFor.current = null
      return
    }
    const key = combo?.product_id ?? 'new'
    if (openedFor.current === key) return
    openedFor.current = key
    const rows = rowsFrom(combo)
    setProductId(combo?.product_id ?? null)
    setName(combo?.name ?? '')
    setPrice(combo ? String(combo.price) : '')
    setCategoryId(combo?.category_id ?? '')
    setDescription(combo?.description ?? '')
    setImageUrl(combo?.image_url ?? '')
    savedImage.current = combo?.image_url ?? ''
    setStorefrontVisible(combo?.is_storefront_visible ?? true)
    setAvailable(combo?.is_available ?? true)
    setComponents(rows.components)
    setSlots(rows.slots)
    setError(null)
  }, [open, combo])

  // Default a new deal to the "Deals" category when there is one.
  const dealsCategory = catalog.categories.find((c) => c.name.trim().toLowerCase() === 'deals')
  useEffect(() => {
    if (open && !combo && !categoryId && dealsCategory) setCategoryId(String(dealsCategory.id))
  }, [open, combo, categoryId, dealsCategory])

  const selfId = productId ?? ''
  const pickable = useMemo(() => catalog.items.filter((p) => String(p.id) !== selfId), [catalog.items, selfId])
  const priceOf = (id: string) => catalog.productPrice(id)

  const priceNum = Number(price)
  const separately = separatePrice(components, slots, priceOf)
  const saving_ = separately - (Number.isFinite(priceNum) ? priceNum : 0)
  const costsMore = Number.isFinite(priceNum) && priceNum > 0 && separately > 0 && priceNum > separately

  const createDealsCategory = async () => {
    setCreatingCategory(true)
    try {
      const res = await apiClient.createCategory({ name: 'Deals', description: 'Combo deals', color: '#FF9F43', sort_order: 0 })
      const id = (res.data as { id?: string } | undefined)?.id
      await qc.invalidateQueries({ queryKey: ['categories'] })
      if (id) setCategoryId(String(id))
      toastHelpers.success('“Deals” category created')
    } catch (e) {
      toastHelpers.error('Could not create the category', errorText(e))
    } finally {
      setCreatingCategory(false)
    }
  }

  const onPhoto = async (f: File | undefined) => {
    if (!f) return
    const url = await menuPhotoFileToDataUrl(f)
    if (url) setImageUrl(url)
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: COMBOS_QUERY_KEY })
    qc.invalidateQueries({ queryKey: ['products'] })
    qc.invalidateQueries({ queryKey: ['admin-products'] })
    qc.invalidateQueries({ queryKey: ['menu-modifiers'] })
    qc.invalidateQueries({ queryKey: ['menu-rules'] })
  }

  const save = async () => {
    setError(null)
    if (!name.trim()) return setError('Give the deal a name')
    if (!Number.isFinite(priceNum) || priceNum <= 0) return setError('Set the deal price')
    if (!categoryId) return setError('Choose a category for the deal')
    const problem = comboDraftProblem(components, slots)
    if (problem) return setError(problem)

    const structure: ComboStructureInput = {
      components: components.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
      slots: slots.map((s) => ({
        ...(s.group_id ? { group_id: s.group_id } : {}),
        name: s.name.trim(),
        choose: s.choose,
        options: s.options.map((o) => ({
          ...(o.option_id ? { option_id: o.option_id } : {}),
          product_id: o.product_id,
          price_delta: Number(o.price_delta) || 0,
        })),
      })),
    }
    const basics = {
      name: name.trim(),
      price: priceNum,
      category_id: categoryId,
      description: description.trim(),
      image_url: imageUrl.trim(),
    }

    setSaving(true)
    let id = productId
    try {
      if (!id) {
        const res = await apiClient.createProduct({ ...basics, preparation_time: 5 })
        id = (res.data as { id?: string } | undefined)?.id ?? null
        if (!id) throw new Error('The deal was not created')
        setProductId(id)
        savedImage.current = basics.image_url
        // Create takes no availability flags; set them only when they differ.
        if (!available || !storefrontVisible) {
          await apiClient.updateProduct(id, { is_available: available, is_storefront_visible: storefrontVisible })
        }
      } else {
        await apiClient.updateProduct(id, {
          ...basics,
          is_available: available,
          is_storefront_visible: storefrontVisible,
          ...mainCutoutPatch(true, savedImage.current, basics.image_url),
        })
        savedImage.current = basics.image_url
      }
    } catch (e) {
      setSaving(false)
      setError(errorText(e, 'Could not save the deal'))
      return
    }
    try {
      await apiClient.putComboStructure(id, structure)
      invalidate()
      toastHelpers.success(combo ? 'Deal saved' : 'Deal created', basics.name)
      onOpenChange(false)
    } catch (e) {
      invalidate()
      setError(errorText(e, 'Could not save what is in the deal'))
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (!productId) return
    setDeleting(true)
    try {
      await apiClient.deleteProduct(productId)
      invalidate()
      toastHelpers.success('Deal deleted', name)
      setConfirmDelete(false)
      onOpenChange(false)
    } catch (e) {
      toastHelpers.error('Could not delete the deal', errorText(e))
    } finally {
      setDeleting(false)
    }
  }

  // ---- row editing ----
  const setComp = (key: string, patch: Partial<RowComponent>) =>
    setComponents((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const setSlot = (key: string, patch: Partial<RowSlot>) =>
    setSlots((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  const setOption = (slotKey: string, optKey: string, patch: Partial<RowOption>) =>
    setSlots((rows) =>
      rows.map((s) => (s.key === slotKey ? { ...s, options: s.options.map((o) => (o.key === optKey ? { ...o, ...patch } : o)) } : s)),
    )

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{combo ? `Edit ${combo.name}` : 'New deal'}</DialogTitle>
          <DialogDescription>
            A deal is one item on the bill at one price. The kitchen ticket lists what is inside it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basics */}
          <section className="space-y-4">
            <div className="flex items-center gap-4">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-20 w-20 shrink-0 rounded-lg border object-cover" />
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-dashed bg-muted/40 text-muted-foreground">
                  <ImageIcon className="h-6 w-6" aria-hidden />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  {imageUrl ? 'Change photo' : 'Add photo'}
                </Button>
                {imageUrl && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setImageUrl('')}>
                    Remove
                  </Button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    void onPhoto(e.target.files?.[0])
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
              <Field label="Name" htmlFor="deal-name">
                <Input id="deal-name" value={name} placeholder="Zinger Deal" onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Deal price" htmlFor="deal-price">
                <Input
                  id="deal-price"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  value={price}
                  placeholder="0"
                  onChange={(e) => setPrice(e.target.value)}
                />
              </Field>
            </div>
            <Field
              label="Category"
              hint={
                !dealsCategory ? (
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    disabled={creatingCategory}
                    onClick={() => void createDealsCategory()}
                  >
                    {creatingCategory ? 'Creating…' : 'Create a “Deals” category'}
                  </button>
                ) : undefined
              }
            >
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {catalog.categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description" htmlFor="deal-desc">
              <Textarea
                id="deal-desc"
                rows={2}
                value={description}
                placeholder="Shown to staff and on the online menu"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch id="deal-available" checked={available} onCheckedChange={setAvailable} />
                <Label htmlFor="deal-available" className="text-sm font-normal">
                  Available to sell
                </Label>
              </div>
              {online.enabled && (
                <div className="flex items-center gap-2">
                  <Switch id="deal-online" checked={storefrontVisible} onCheckedChange={setStorefrontVisible} />
                  <Label htmlFor="deal-online" className="text-sm font-normal">
                    Show on online store
                  </Label>
                </div>
              )}
            </div>
          </section>

          {/* Fixed items */}
          <section className="space-y-3 border-t pt-5">
            <div>
              <h3 className="text-base font-semibold">Always included</h3>
              <p className="text-sm text-muted-foreground">Items that come with every deal, for example the burger.</p>
            </div>
            {components.length === 0 && <p className="text-sm text-muted-foreground">No fixed items — the deal is only choices.</p>}
            {components.map((c) => (
              <div key={c.key} className="flex items-start gap-2">
                <ProductPicker
                  products={pickable}
                  value={c.product_id}
                  onChange={(id) => setComp(c.key, { product_id: id })}
                  money={formatCurrency}
                  exclude={new Set(components.filter((x) => x.key !== c.key).map((x) => x.product_id))}
                />
                <Input
                  type="number"
                  min={1}
                  max={50}
                  aria-label="Quantity"
                  className="w-20"
                  value={c.quantity}
                  onChange={(e) => setComp(c.key, { quantity: Math.max(1, Math.floor(Number(e.target.value) || 1)) })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove item"
                  onClick={() => setComponents((rows) => rows.filter((r) => r.key !== c.key))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setComponents((rows) => [...rows, { key: uid(), product_id: '', quantity: 1 }])}
            >
              <Plus className="h-4 w-4" />
              Add item
            </Button>
          </section>

          {/* Choices */}
          <section className="space-y-3 border-t pt-5">
            <div>
              <h3 className="text-base font-semibold">Choices</h3>
              <p className="text-sm text-muted-foreground">
                The guest picks from these, for example “Choose a side” or “Choose a drink”. Add an upgrade price for a bigger option.
              </p>
            </div>
            {slots.map((s) => (
              <div key={s.key} className="space-y-3 rounded-lg border p-4">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[12rem] flex-1">
                    <Field label="Choice name">
                      <Input value={s.name} placeholder="Choose a drink" onChange={(e) => setSlot(s.key, { name: e.target.value })} />
                    </Field>
                  </div>
                  <div className="w-36">
                    <Field label="Customer picks">
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={s.choose}
                        onChange={(e) => setSlot(s.key, { choose: Math.max(1, Math.floor(Number(e.target.value) || 1)) })}
                      />
                    </Field>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove choice"
                    onClick={() => setSlots((rows) => rows.filter((r) => r.key !== s.key))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Options</p>
                  {s.options.length === 0 && <p className="text-sm text-muted-foreground">Add at least one option.</p>}
                  {s.options.map((o) => (
                    <div key={o.key} className="flex items-start gap-2">
                      <ProductPicker
                        products={pickable}
                        value={o.product_id}
                        onChange={(id) => setOption(s.key, o.key, { product_id: id })}
                        money={formatCurrency}
                        exclude={new Set(s.options.filter((x) => x.key !== o.key).map((x) => x.product_id))}
                      />
                      <div className="relative w-32">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">+</span>
                        <Input
                          type="number"
                          min={0}
                          aria-label="Upgrade price"
                          className="pl-7"
                          value={o.price_delta === 0 ? '' : o.price_delta}
                          placeholder="0"
                          onChange={(e) => setOption(s.key, o.key, { price_delta: Math.max(0, Number(e.target.value) || 0) })}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove option"
                        onClick={() => setSlot(s.key, { options: s.options.filter((x) => x.key !== o.key) })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setSlot(s.key, { options: [...s.options, { key: uid(), product_id: '', price_delta: 0 }] })}
                  >
                    <Plus className="h-4 w-4" />
                    Add option
                  </Button>
                  <p className="text-xs text-muted-foreground">Upgrade price is added to the deal price, e.g. +Rs 50 for a large drink. Leave it empty for no extra charge.</p>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setSlots((rows) => [
                  ...rows,
                  { key: uid(), name: rows.length === 0 ? 'Choose a side' : 'Choose a drink', choose: 1, options: [] },
                ])
              }
            >
              <Plus className="h-4 w-4" />
              Add choice
            </Button>
          </section>

          {/* Summary */}
          <section className="space-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Deal price</span>
              <span className="font-medium tabular-nums">{Number.isFinite(priceNum) && priceNum > 0 ? formatCurrency(priceNum) : '—'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Bought separately (cheapest choices)</span>
              <span className="tabular-nums">{formatCurrency(separately)}</span>
            </div>
            {Number.isFinite(priceNum) && priceNum > 0 && separately > 0 && !costsMore && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Guest saves</span>
                <span className="font-medium tabular-nums text-emerald-700 dark:text-emerald-400">{formatCurrency(saving_)}</span>
              </div>
            )}
            {costsMore && (
              <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                This deal costs {formatCurrency(priceNum - separately)} more than buying the items separately. You can still save it.
              </p>
            )}
          </section>

          {error && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {productId && (
            <Button type="button" variant="ghost" className="text-destructive sm:mr-auto" onClick={() => setConfirmDelete(true)} disabled={saving}>
              Delete deal
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : productId ? 'Save deal' : 'Create deal'}
          </Button>
        </DialogFooter>

        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={`Delete ${name || 'this deal'}?`}
          description="The deal comes off the menu. Past bills keep their record. This cannot be undone."
          confirmLabel="Delete deal"
          busy={deleting}
          onConfirm={() => void doDelete()}
        />
      </DialogContent>
    </Dialog>
  )
}
